/**
 * This module exports a function that returns an object with a
 * `validate` method. The `validate` method takes a value to test
 * and a validation object, and returns a boolean indicating whether
 * the value passes the validation.
 *
 * The validation object contains a `type` property that specifies
 * the type of validation to perform, and additional properties that
 * provide parameters for the validation.
 *
 * The available validation types include boolean, string, number,
 * and date validators, as well as crossfield validators that compare
 * the value to another field in the form.
 *
 * Each validator is implemented as a private function that takes a
 * value to test and a validation object, and returns a boolean
 * indicating whether the value passes the validation.
 *
 * The module exports a factory function that returns a new instance
 * of the `validate` object each time it is called, allowing multiple
 * forms to use the validation logic independently.
 *
 * @author Luca Margadant
 * @contact luca.margadant@nueva.ch
 * @copyright Copyright (c) Nueva AG
 */
const { dayjs } = useDayJs();

const validators = {
    /*
        Boolean validators
    */

    /*
        {
            type: 'isNotEmpty',
            message: 'This field is required',
        },
    */
    _isNotEmpty: (value) => (!!value && value !== ''),

    /*
        {
            type: 'isTrue',
            message: 'This field needs to be boolean with true value',
        },
   */
    _isTrue: (value) => value === true,

    /*
        {
            type: 'isFalse',
            message: 'This field needs to be boolean with false value',
        },
   */
    _isFalse: (value) => value === false,

    /*
        {
            type: 'isNot',
            message: 'This field needs to be different from "example"',
            value: 'example',
        },
    */
    _isNot: (value, params) => value !== params.value,

    /*
        {
            type: 'isSame',
            message: 'This field needs to be the same as "example"',
            value: 'example',
        },
    */
    _isSame: (value, params) => value === params.value,

    /*
        {
            type: 'isNumber',
            message: 'This field needs to be a number',
        },
    */
    _isNumber: (value) => !Number.isNaN(value),

    /*
        {
            type: 'isDate',
            message: 'This field needs to be a date',
        },
    */
    _isDate: (value) => !Number.isNaN(Date.parse(value)),

    /*
        {
            type: 'isInteger',
            message: 'This field needs to be an integer',
        },
    */
    _isInteger: (value) => Number.isInteger(value),

    /*
        {
            type: 'isString',
            message: 'This field needs to be a string',
        },
    */
    _isString: (value) => typeof value === 'string',

    /*
        {
            type: 'isArray',
            message: 'This field needs to be an array',
        },
    */
    _isArray: (value) => Array.isArray(value),

    /*
        {
            type: 'isBoolean',
            message: 'This field needs to be a boolean',
        },
    */
    _isBoolean: (value) => typeof value === 'boolean',

    /*
        String validators
    */
    /*
        {
            type: 'minLength',
            message: 'This field needs to have at least 5 characters',
            value: 5,
        },
    */
    _minLength: (value, params) => value.length >= params.value,

    /*
        {
            type: 'maxLength',
            message: 'This field needs to have at most 10 characters',
            value: 10,
        },
    */
    _maxLength: (value, params) => value.length <= params.value,
    
    /*
        {
            type: 'exactLength',
            message: 'This field needs to have exactly 10 characters',
            value: 10,
        },
    */
    _exactLength: (value, params) => value.length === params.value,

    /*
        {
            type: 'minWords',
            message: 'This field needs to have at least 5 words',
            value: 5,
        },
    */
    _minWords: (value, params) => value.split(' ').length >= params.value,

    /*
        {
            type: 'maxWords',
            message: 'This field needs to have at most 10 words',
            value: 10,
        },
    */
    _maxWords: (value, params) => value.split(' ').length <= params.value,

    /*
        {
            type: 'minChars',
            message: 'This field needs to have at least 5 characters',
            value: 5,
        },
    */
    _minChars: (value, params) => value.replace(/\s/g, '').length >= params.value,

    /*
        {
            type: 'maxChars',
            message: 'This field needs to have at most 10 characters',
            value: 10,
        },
    */
    _maxChars: (value, params) => value.replace(/\s/g, '').length <= params.value,

    /*
        {
            type: 'isIn',
            message: 'This field needs to be one of: "example1", "example2", "example3"',
            value: ['example1', 'example2', 'example3'],
        },
    */
    _isIn: (value, params) => params.value.includes(value),

    /*
        {
            type: 'isNotIn',
            message: 'This field needs to be different from: "example1", "example2", "example3"',
            value: ['example1', 'example2', 'example3'],
        },
    */
    _isNotIn: (value, params) => !params.value.includes(value),

    /*
        {
            type: 'isUuid',
            message: 'This field needs to be a valid UUID',
        },
    */
    _isUuid: (value) => /^[a-f\d]{8}(-[a-f\d]{4}){4}[a-f\d]{8}$/i.test(value),

    /*
        {
            type: 'isSlug',
            message: 'This field needs to be a valid slug',
        },
    */
    _isSlug: (value) => /^[a-z0-9]+(?:-[a-z0-9]+)*$/.test(value),

    /*
        {
            type: 'isUrl',
            message: 'This field needs to be a valid URL',
        },
    */
    _isUrl: (value) => /^(?:\w+:)?\/\/([^\s.]+\.\S{2}|localhost[:?\d]*)\S*$/.test(value),

    /*
        {
            type: 'isAlphabetic',
            message: 'This field needs to be alphabetic',
        },
    */
    _isAlphabetic: (value) => /[A-Za-zÀ-ÖØ-öø-ÿ ]/.test(value),

    /*
        {
            type: 'isAlphanumeric',
            message: 'This field needs to be alphanumeric',
        },
    */
    _isAlphanumeric: (value) => /[A-Za-zÀ-ÖØ-öø-ÿ0-9 ]/.test(value),

    /*
        {
            type: 'isNumeric',
            message: 'This field needs to be numeric',
        },
    */
    _isNumeric: (value) => {
        if (value === '') return true;
        return /^\d+$/.test(value);
    },

    /*
        {
            type: 'isPhoneNumber',
            message: 'This field needs to be a valid phone number',
        },
    */
    _isPhoneNumber: (value) => {
        if (value === '') return true;
        return /^[0-9+\-() ]+$/.test(value);
    },

    /*
        {
            type: 'isEmail',
            message: 'This field needs to be a valid email',
        },
    */
    _isEmail: (value) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value),

    /*
        Number validators
    */
    /*
        {
            type: 'minValue',
            message: 'This field needs to be at least 5',
            value: 5,
        },
    */
    _minValue: (value, params) => value >= params.value,

    /*
        {
            type: 'maxValue',
            message: 'This field needs to be at most 10',
            value: 10,
        },
    */
    _maxValue: (value, params) => value <= params.value,

    /*
        {
            type: 'betweenValues',
            message: 'This field needs to be between 5 and 10',
            value: {
                min: 5,
                max: 10,
            },
        },
    */
    _betweenValues: (value, params) => {
        const { min, max } = params.value;
        return value >= min && value <= max;
    },

    /*
        {
            type: 'minAge',
            message: 'You need to be at least 18 years old',
            value: 18,
        },
    */
    _minAge: (value, params) => {
        if (value === 'Invalid Date' || !value) return true;
        const birthdatePlus18Years = dayjs(value).add(params.value, 'year');
        return birthdatePlus18Years.isBefore(dayjs()) || birthdatePlus18Years.isSame(dayjs());
    },

    /*
        WIP: Date validators TODO Test these when adding date support
    */
    /*
        {
            type: 'minDate',
            message: 'This field needs to be at least 1. January 2020',
            value: '2020-01-01',
        },
    */
    _minDate: (value, params) => Date.parse(value) >= Date.parse(params.value),

    /*
        {
            type: 'maxDate',
            message: 'This field needs to be at most 1. January 2020',
            value: '2020-01-01',
        },
    */
    _maxDate: (value, params) => Date.parse(value) <= Date.parse(params.value),

    /*
        {
            type: 'betweenDates',
            message: 'This field needs to be between 1. January 2020 and 1. January 2021',
            value: {
                min: '2020-01-01',
                max: '2021-01-01',
            },
        },
    */
    _betweenDates: (value, params) => {
        const { min, max } = params.values;
        return Date.parse(value) >= min && Date.parse(value) <= max;
    },

    /*
        WIP: Crossfield validators TODO Test these when adding crossfield support
    */
    /*
        {
            type: 'isSameAs',
            message: 'This field needs to be the same as the field with id "example"',
            value: 'example',
        },
    */
    _isSameAs: (value, params) => {
        const element = document.querySelector(`#${params.value}`);
        if (!element) return false;
        return validators._isSame(value, element);
    },

    /*
        {
            type: 'isNotSameAs',
            message: 'This field needs to be different from the field with id "example"',
            value: 'example',
        },
    */
    _isNotSameAs: (value, params) => {
        const element = document.querySelector(`#${params.value}`);
        if (!element) return false;
        return validators._isNot(value, element);
    },
};

export default () => {
    const validate = (
        valueToTest,
        validationObj,
    ) => validators[`_${validationObj.type}`](valueToTest, validationObj);

    return {
        validate,
    };
};
